import {Component, DestroyRef, EventEmitter, Input, OnInit, Output, ChangeDetectionStrategy} from '@angular/core';
import {AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';

import {NewPoll, Poll} from '../../_types/polls';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {debounceTime, take} from 'rxjs';
import {CdkDragDrop, moveItemInArray} from "@angular/cdk/drag-drop";

import {MatCheckboxChange} from "@angular/material/checkbox";

import {removeNullUndefinedSpecificKeyFieldsRecursive} from '../../_utils/remove-null-fields';
import {PollsService} from '../../_services/polls.service';
import {mergeDeep} from '../../_utils/utils';
import {SnackbarService} from '../../_services/snackbar.service';
import {Community} from "../../_types/communities";
import {ThemeService} from "../../_services/theme.service";
import {CommunitiesService} from "../../_services/communities.service";


@Component({
    selector: 'app-create-poll',
    templateUrl: './create-poll.component.html',
    styleUrl: './create-poll.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CreatePollComponent implements OnInit {
    @Input() ai_comment_builder?: boolean = false;
    identifiers: string[] = []
    formGroup: FormGroup = this.fb.group({
        text: this.fb.control(''),
        description: this.fb.control(''),
        prompt: this.fb.control(''),
        end_at: this.fb.control(null),
        scale: this.fb.control(false),
        matrix_exclusive: this.fb.control(false),
        ai_comment_builder: this.fb.control(false),
        shuffle_questions: this.fb.control(false),
        questions: this.fb.array([
            this.newQuestion(),
        ])
    });
    @Output() isValidPollData: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() pollData: EventEmitter<NewPoll> = new EventEmitter<NewPoll>();
    @Output() pollUpdated: EventEmitter<Poll> = new EventEmitter<Poll>();
    @Output() pollUpdateCanceled: EventEmitter<void> = new EventEmitter<void>();
    initialPoll?: Poll
    editMode: boolean = false;
    sending: boolean = false;
    scaleAmount: number = 5;
    // Scale numbers needs to start at the number of questions and have at least the number of questions in numbers
    scaleNumbers: number[] = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10]
    // To do this the scale numbers need to change when a question is added or removed or when matrix_exclusive is toggled on or off

    community: Community | null = null;


    constructor(
        private readonly fb: FormBuilder,
        private readonly pollsService: PollsService,
        private readonly snackbarService: SnackbarService,
        private readonly communitiesService: CommunitiesService,
        protected themeService: ThemeService,
        private destroyRef: DestroyRef
    ) {
        this.isValidPollData.emit(false);
        this.formGroup.statusChanges.pipe(takeUntilDestroyed()).subscribe(res => {
            if (res == 'VALID') {
                const questionsFormArray: FormArray<any> = this.questions();
                let valid: boolean = true;
                for (let questionIndex = 0; questionIndex < questionsFormArray.controls.length; questionIndex++) {
                    const questionControl: AbstractControl = questionsFormArray.controls[questionIndex];
                    const type = (questionControl as any).get('type')?.value;
                    if (type === 'lists') {
                        if (!this.checkIfAnyNestedItems(questionIndex)) {
                            valid = false;
                            break;
                        } else if (!this.checkIfListIsPossible(questionIndex)) {
                            valid = false;
                            break;
                        }
                    }
                }

                this.isValidPollData.emit(valid);
            } else {
                this.isValidPollData.emit(false);
            }
        });
        this.formGroup.valueChanges.pipe(
            debounceTime(200),
            takeUntilDestroyed()
        ).subscribe(() => {
            this.pollData.emit(removeNullUndefinedSpecificKeyFieldsRecursive(this.formGroup.value, ['id']));
        });

        this.formGroup.get('matrix_exclusive')!.valueChanges
            .subscribe(r => {
                this.updateScaleNumbers();
            })
        this.communitiesService.getCurrentDashboardCommunity()
            .pipe(takeUntilDestroyed())
            .subscribe(community => {
                this.community = community;
            });
    }

    @Input() set initialPollData(value: Poll | undefined) {
        if (!value) return;
        this.initialPoll = value;
        this.removeQuestion(0);
        Array.from({length: value.questions.length}, (): void => this.addQuestion(false));
        value.questions.forEach((question, i): void => {
            Array.from({length: question.options.length}, (): void => this.addOption(i));
            question.options.forEach((option, j): void => {
                Array.from({length: option.nested_options.length}, (): void => this.addNestedOption(i, j, false));
            })
        })

        this.formGroup.patchValue(value);
        this.questions().controls.forEach((question, i): void => {
            question.get('display_condition_options_identifiers')?.patchValue(value.questions[i].display_condition_options_identifiers_list)
            question.get('display_condition_options_identifiers')?.disable();
            question.get('max_responses')?.disable();
            this.options(i).controls.forEach((option, j): void => {
                option.get('max_nested_responses')?.disable();
            })
        })
        this.formGroup.updateValueAndValidity();
        this.disableAllCheckboxes(this.formGroup);
        this.editMode = true;
    };

    get isScale(): boolean {
        return this.formGroup.get('scale')?.value || false
    }

    get isMatrixExclusive(): boolean {
        return this.formGroup.get('matrix_exclusive')?.value || false
    }

    ngOnInit(): void {
        if (this.ai_comment_builder) {
            this.formGroup.patchValue({
                ai_comment_builder: true
            });
        }
    }

    getQuestionType(questionId: number): string {
        return this.question(questionId).get('type')?.value
    }

    questions(): FormArray {
        return this.formGroup.get('questions') as FormArray;
    }

    question(questionIndex: number): AbstractControl {
        return this.questions().at(questionIndex)
    }

    getOption(questionIndex: number, optionIndex: number): AbstractControl {
        return this.options(questionIndex).at(optionIndex)
    }

    options(questionIndex: number): FormArray {
        return this.questions()
            .at(questionIndex)
            .get('options') as FormArray;
    }

    nestedOptions(questionIndex: number, optionIndex: number): FormArray {
        return this.options(questionIndex)
            .at(optionIndex)
            .get('nested_options') as FormArray;
    }

    guidGenerator(): string {
        const S4: () => string = function (): string {
            return (((1 + Math.random()) * 0x10000) | 0).toString(16).substring(1);
        };
        const guid: string = (S4() + S4() + "-" + S4() + "-" + S4() + "-" + S4() + "-" + S4() + S4() + S4());

        if (this.identifiers.includes(guid)) {
            return this.guidGenerator();
        } else {
            this.identifiers.push(guid)
            return guid
        }
    }

    removeQuestion(i: number): void {
        const questionCtrl = this.questions().at(i);
        const identifiersToRemove = this.getAllIdentifiersFromQuestion(questionCtrl);

        this.removeDisplayConditionIdentifiersEverywhere(identifiersToRemove);
        this.questions().removeAt(i);
        this.updateScaleNumbers();
    }

    addQuestion(initOptions: boolean = true): void {
        this.questions().push(this.newQuestion(initOptions));
        this.updateScaleNumbers();
    }

    removeOption(questionId: number, i: number): void {
        const optionCtrl = this.getOption(questionId, i);
        const identifiersToRemove = this.getAllIdentifiersFromOption(optionCtrl);

        this.removeDisplayConditionIdentifiersEverywhere(identifiersToRemove);

        if (optionCtrl.get('is_other_option')?.value) {
            this.question(questionId).get('other_option')?.setValue(false, {emitEvent: false});
        }

        this.options(questionId).removeAt(i);
    }

    addOption(questionId: number): void {
        this.options(questionId).push(this.newOption());
    }

    typeChange(questionIndex: number): void {
        const questionCtrl = this.question(questionIndex);
        const identifiersToRemove = this.getAllIdentifiersFromQuestion(questionCtrl);
        this.removeDisplayConditionIdentifiersEverywhere(identifiersToRemove);

        for (let index: number = this.options(questionIndex).length - 1; index >= 0; index--) {
            this.options(questionIndex).removeAt(index);
        }

        setTimeout((): void => {
            this.options(questionIndex).push(
                this.newOption(undefined, this.question(questionIndex).get('type')?.value == 'click_game' ? 22 : 150)
            );
            this.options(questionIndex).push(
                this.newOption(undefined, this.question(questionIndex).get('type')?.value == 'click_game' ? 22 : 150)
            );
        }, 100);
    }

    likertScale(event: MatCheckboxChange): void {
        const allIdentifiersToRemove = this.questions().controls.flatMap(questionCtrl => this.getAllIdentifiersFromQuestion(questionCtrl));
        this.removeDisplayConditionIdentifiersEverywhere(allIdentifiersToRemove);

        this.questions().clear();

        if (event.checked) {
            this.formGroup.patchValue({
                scale: true
            })
            this.addQuestion()
        } else {
            this.formGroup.patchValue({
                scale: false,
                matrix_exclusive: false,
                shuffle_questions: false
            })
            this.addQuestion(true)
        }
    }

    scaleUpdate(): void {
        const allIdentifiersToRemove = this.questions().controls.flatMap(questionCtrl => this.getAllIdentifiersFromQuestion(questionCtrl));
        this.removeDisplayConditionIdentifiersEverywhere(allIdentifiersToRemove);

        for (let index: number = 0; index < this.questions().length; index++) {
            this.options(index).clear();
            for (let i: number = 1; i < this.scaleAmount + 1; i++) {
                this.options(index).push(this.newOption(JSON.stringify(i)));
            }
        }
    }

    removeNestedOption(questionId: number, optionId: number, i: number): void {
        const nestedCtrl = this.nestedOptions(questionId, optionId).at(i);
        const identifier = nestedCtrl.get('identifier')?.value;

        if (identifier) {
            this.removeDisplayConditionIdentifierEverywhere(identifier);
        }

        this.nestedOptions(questionId, optionId).removeAt(i);
    }

    addNestedOption(questionId: number, optionId: number, firstAdd?: boolean): void {
        this.nestedOptions(questionId, optionId).push(this.newNestedOption());
        if (firstAdd) {
            // 2 is minimum number of options
            this.nestedOptions(questionId, optionId).push(this.newNestedOption());
        }
    }

    removeNestedPoll(questionId: number, optionId: number): void {
        const optionCtrl = this.getOption(questionId, optionId);
        const nestedOptions = optionCtrl.get('nested_options') as FormArray;
        const identifiersToRemove: string[] = nestedOptions.controls
            .map(ctrl => ctrl.get('identifier')?.value)
            .filter((id): id is string => !!id);

        this.removeDisplayConditionIdentifiersEverywhere(identifiersToRemove);
        nestedOptions.clear();
    }

    drop(event: CdkDragDrop<string[]>, questionId: number): void {
        moveItemInArray(this.options(questionId).controls, event.previousIndex, event.currentIndex);
    }

    dropNested(event: CdkDragDrop<string[]>, questionId: number, optionId: number): void {
        moveItemInArray(this.nestedOptions(questionId, optionId).controls, event.previousIndex, event.currentIndex);
    }

    updatePoll(): boolean {
        this.sending = true;
        const updatedPoll = mergeDeep(this.initialPoll, this.formGroup.value);
        const updatedPollOrg = JSON.parse(JSON.stringify(updatedPoll));
        ['author', 'post', 'total_votes', 'user_vote'].forEach(key => {
            if (key in updatedPoll) {
                delete updatedPoll[key];
            }
        });

        this.pollsService.editPoll(updatedPoll).pipe(take(1))
            .subscribe({
                next: (): void => {
                    this.sending = false;
                    this.snackbarService.openSuccessSnackBar('Poll has been successfully updated.');
                    this.pollUpdated.emit(updatedPollOrg);
                },
                error: (err: string): void => {
                    this.sending = false;
                    this.snackbarService.openErrorSnackBar(err);
                }
            })
        return false;
    }

    updateScaleNumbers(): void {
        if (this.isMatrixExclusive) {
            this.scaleNumbers = [];

            for (let i: number = 1; i <= (this.questions().length < 10 ? 10 : this.questions().length); i++) {
                // The minimum amount of options is 10 but if there are more then 10 questions then the numbers must at least match.
                this.scaleNumbers.push(i)
            }
            // You cannot have numbers below the number of questions
            this.scaleNumbers = this.scaleNumbers?.filter(
                (num): boolean => num >= this.questions().length
            );

        } else if (JSON.stringify(this.scaleNumbers) != JSON.stringify([1, 2, 3, 4, 5, 6, 7, 8, 9, 10])) {
            // Reset if isMatrixExclusive has been removed. But don't reset if not needed
            this.scaleNumbers = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
        }
        if (this.isScale) {
            if (!this.scaleNumbers.includes(this.scaleAmount)) {
                // if the current selected value has been removed then select a new one
                this.scaleAmount = this.scaleNumbers[0];
            }
            this.scaleUpdate()
        }
    }

    onColourChange(newColour: string, questionIndex: number, optionIndex: number): void {
        const colourControl = this.getOption(questionIndex, optionIndex).get('colour');
        if (colourControl) {
            colourControl.setValue(newColour);
            // Optional: Mark as touched or dirty if needed for validation/change tracking
            colourControl.markAsDirty();
            colourControl.markAsTouched();
        }
    }

    currencySliderFormat(value: number): string {
        if (value === null) {
            return '0.00';
        } else {
            return value.toFixed(2);
        }
    }

    defaultSliderFormat(value: number): string {
        if (value === null) {
            return '0';
        } else {
            return String(value);
        }
    }

    checkIfAnyNestedItems(questionIndex: number): boolean {
        const optionsFormArray: FormArray<any> = this.options(questionIndex);
        let valid: boolean = false;
        optionsFormArray.controls.forEach((optionControl: AbstractControl, optionIndex: number): void => {
            if (this.nestedOptions(questionIndex, optionIndex).length > 0) {
                valid = true;
            }
        });

        return valid;
    }

    checkIfListIsPossible(questionIndex: number): boolean {
        let valid: boolean = true;
        const optionsFormArray: FormArray<any> = this.options(questionIndex);

        let totalMinimum: number = 0;
        let totalMaximum: number = 0;
        let totalNestedOptions: number = 0;
        let noMaximumRoof: boolean = false

        for (let optionIndex = 0; optionIndex < optionsFormArray.controls.length; optionIndex++) {
            const option: AbstractControl = optionsFormArray.controls[optionIndex];

            const maximum = (option as any).get('slider_max')?.value;
            const minimum = (option as any).get('slider_min')?.value;
            totalNestedOptions = totalNestedOptions + this.nestedOptions(questionIndex, optionIndex).length

            if (maximum == null) {
                noMaximumRoof = true;
            } else {
                // at least one list has no maximum so no need to check
                totalMaximum = totalMaximum + maximum;
            }
            if (minimum) {
                totalMinimum = totalMinimum + minimum;
            }
        }

        // need to check that the minimum amount allowed across all items is greater than the number of nested options
        if (totalMinimum > totalNestedOptions) {
            valid = false;
        }

        // need to check that the maximum amount allowed across all items is less than the number of nested options
        if (!noMaximumRoof && (totalMaximum < totalNestedOptions)) {
            valid = false;
        }

        return valid
    }

    private removeDisplayConditionIdentifierEverywhere(identifier: string): void {
        this.questions().controls.forEach((questionCtrl) => {
            const control = questionCtrl.get('display_condition_options_identifiers');
            if (!control) return;

            const current: string[] = control.value || [];
            const next = current.filter(id => id !== identifier);

            if (next.length !== current.length) {
                control.patchValue(next, {emitEvent: false});
            }
        });
    }

    private removeDisplayConditionIdentifiersEverywhere(identifiers: string[]): void {
        if (!identifiers.length) return;

        const invalidIds = new Set(identifiers);

        this.questions().controls.forEach((questionCtrl) => {
            const control = questionCtrl.get('display_condition_options_identifiers');
            if (!control) return;

            const current: string[] = control.value || [];
            const next = current.filter(id => !invalidIds.has(id));

            if (next.length !== current.length) {
                control.patchValue(next, {emitEvent: false});
            }
        });
    }

    private getAllIdentifiersFromOption(optionCtrl: AbstractControl): string[] {
        const identifiers: string[] = [];

        const optionId = optionCtrl.get('identifier')?.value;
        if (optionId) {
            identifiers.push(optionId);
        }

        const nestedOptions = optionCtrl.get('nested_options') as FormArray | null;
        if (nestedOptions) {
            nestedOptions.controls.forEach((nestedCtrl) => {
                const nestedId = nestedCtrl.get('identifier')?.value;
                if (nestedId) {
                    identifiers.push(nestedId);
                }
            });
        }

        return identifiers;
    }

    private getAllIdentifiersFromQuestion(questionCtrl: AbstractControl): string[] {
        const identifiers: string[] = [];
        const options = questionCtrl.get('options') as FormArray | null;

        if (!options) {
            return identifiers;
        }

        options.controls.forEach((optionCtrl) => {
            identifiers.push(...this.getAllIdentifiersFromOption(optionCtrl));
        });

        return identifiers;
    }

    private newQuestion(initOptions: boolean = true): FormGroup {
        let fbArray: FormGroup<any>[] = initOptions ? [this.newOption(), this.newOption()] : [];
        if (this.formGroup?.value?.scale) {
            fbArray = [];
            for (let i = 1; i < this.scaleAmount + 1; i++) {
                fbArray.push(this.newOption(JSON.stringify(i)))
            }
        }
        const questionGroup = this.fb.group({
            id: this.fb.control(null),
            text: this.fb.control('', Validators.required),
            multiple_choices: this.fb.control(false),
            randomize_options: this.fb.control(false),
            disable_animation: this.fb.control(false),
            other_option: this.fb.control(false),
            display_condition_options_identifiers: this.fb.control([]),
            max_responses: this.fb.control(null, Validators.min(2)),
            options: this.fb.array(fbArray),
            type: this.fb.control('select'),
        });


        questionGroup.get('type')!.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
            (): void => {
                const identifiersToRemove = this.getAllIdentifiersFromQuestion(questionGroup);
                this.removeDisplayConditionIdentifiersEverywhere(identifiersToRemove);
            }
        );

        questionGroup.get('other_option')!.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
            (value): void => {
                const optionsArray = questionGroup.get('options') as FormArray;
                if (value) {
                    optionsArray.push(this.newOption('Other', 150, true));
                } else {
                    const otherIndex = optionsArray.controls.findIndex(
                        (option) => option.get('is_other_option')?.value
                    );
                    if (otherIndex !== -1) {
                        const otherOption = optionsArray.at(otherIndex);
                        const identifiersToRemove = this.getAllIdentifiersFromOption(otherOption);
                        this.removeDisplayConditionIdentifiersEverywhere(identifiersToRemove);
                        optionsArray.removeAt(otherIndex);
                    }
                }
            }
        );

        return questionGroup;
    }

    private newOption(defaultText?: string, maxLength?: number, otherOption?: boolean): FormGroup {
        return this.fb.group({
            id: this.fb.control(null),
            identifier: this.fb.control(this.guidGenerator()),
            text: this.fb.control(defaultText || '', [Validators.required, Validators.maxLength(maxLength || 150)]),
            description: this.fb.control(otherOption ? 'If selected please specify in the textbox below' : ''),
            not_applicable: this.fb.control(false),
            fixed: this.fb.control(false),
            is_other_option: this.fb.control(!!otherOption),
            randomize_nested_options: this.fb.control(false),
            nested_multiple_choices: this.fb.control(false),
            disable_shadows: this.fb.control(false),
            disable_text_background: this.fb.control(false),
            hide_in_results: this.fb.control(false),
            max_nested_responses: this.fb.control(null, Validators.min(2)),
            nested_question_title: this.fb.control(''),
            nested_options: this.fb.array([]),
            slider_max: this.fb.control(null, Validators.min(0)),
            slider_min: this.fb.control(null, Validators.min(0)),
            slider_start_label: this.fb.control(''),
            slider_step: this.fb.control(1),
            slider_middle_label: this.fb.control(''),
            slider_end_label: this.fb.control(''),
            shape: this.fb.control('balloon', Validators.required),
            click_amount: this.fb.control(3, [Validators.min(0), Validators.required]),
            colour: this.fb.control(this.community?.accent_color_hex || '#ffe1aa'),
        });
    }

    private newNestedOption(): FormGroup {
        return this.fb.group({
            id: this.fb.control(null),
            text: this.fb.control('', Validators.required),
            description: this.fb.control(''),
            not_applicable: this.fb.control(false),
            fixed: this.fb.control(false),
            identifier: this.fb.control(this.guidGenerator()),
        });
    }

    private disableAllCheckboxes(formGroup: FormGroup | FormArray): void {
        Object.keys(formGroup.controls).forEach(key => {
            const control = formGroup.get(key);

            if (control instanceof FormGroup || control instanceof FormArray) {
                this.disableAllCheckboxes(control);
            } else if (control instanceof FormControl && control.value !== null && typeof control.value === 'boolean') {
                control.disable();
            }
        });
    }
}
